

































































































































import { Facet } from '~/models/search/types'
import {
  CLASSIFIED_SEARCH_NS,
  SearchState
} from '~/store/modules/shared/classifieds/search/state'
import ClassifiedSearchGroupPriceHandlerLeasing from '~/components/car/classifieds/search/facets/handlers/ClassifiedSearchGroupPriceHandlerLeasing.vue'
import ClassifiedSearchGroupPriceHandlerInstallments from '~/components/car/classifieds/search/facets/handlers/ClassifiedSearchGroupPriceHandlerInstallments.vue'
import {
  emptyInstallmentsParams,
  emptyLeasingParams
} from '~/constants/search/facets'
import CRadioButtonGroup from '~/components/shared/configurable/button/CRadioButtonGroup.vue'
import { defineComponent, ref, computed, watch } from '~/utils/nuxt3-migration'
import { facetHandlerComponentProps } from '~/constants/classified/search/facet'
import { useNamespacedStore } from '~/compositions/store'
import useClassifiedSearchFacet from '~/compositions/classified-search/useClassifiedSearchFacet'
import { useI18n } from '~/compositions/i18n'
import COptionWithCount from '~/components/shared/configurable/form/option/COptionWithCount.vue'
import { ciCart } from '~/icons/source/solid/cart'
import { defineComponentTranslations } from '~/utils/i18n'

type PurchaseType = 'price' | 'with_instalments' | 'leasing'

export default defineComponent({
  props: facetHandlerComponentProps,
  components: {
    ClassifiedSearchGroupPriceHandlerInstallments,
    COptionWithCount,
    CRadioButtonGroup,
    ClassifiedSearchGroupPriceHandlerLeasing
  },
  setup(props) {
    const {
      state: classifiedSearchState,
      getters: classifiedSearchGetters
    } = useNamespacedStore<SearchState>(CLASSIFIED_SEARCH_NS)
    const { t } = useI18n()
    const { changeParam, changeParams } = useClassifiedSearchFacet({
      onClearAll: () => {
        selectedPurchaseType.value = 'price'
      }
    })

    const financeOptionsFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('finance_options')
    )

    const financeFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('finance')
    )

    const withPriceFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('with_price')
    )

    const discountFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('discount')
    )

    const wholesalePriceFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('wholesale_price')
    )

    const dealersOnlyFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('visibility')
    )

    const cheapOnlyFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('cheap_only')
    )

    const marketplaceFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('marketplace')
    )

    const leasingOptionsFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('leasing_options')
    )

    const leasingExtrasFacet = computed(() =>
      classifiedSearchGetters('getFacetByName')('leasing_extras')
    )

    const selectedPriceFrom = ref(props.selected.low || null)
    const selectedPriceTo = ref(props.selected.high || null)
    const selectedPurchaseType = ref<PurchaseType>(
      getSelectedPurchaseType(financeFacet.value?.values?.selected)
    )
    const isWithPriceOnlySelected = ref(
      withPriceFacet.value?.isSelected || false
    )
    const isWithDiscountsOnlySelected = ref(
      discountFacet.value?.isSelected || false
    )
    const isWholesalePriceOnlySelected = ref(
      wholesalePriceFacet.value?.isSelected || false
    )
    const isDealersOnlySelected = ref(
      dealersOnlyFacet.value?.isSelected || false
    )
    const isCheapOnlySelected = ref(cheapOnlyFacet.value?.isSelected || false)
    const isMarketplaceSelected = ref(
      marketplaceFacet.value?.isSelected || false
    )

    watch(financeFacet, (newFinanceFacet: Facet) => {
      selectedPurchaseType.value = getSelectedPurchaseType(
        newFinanceFacet?.values?.selected
      )
    })

    watch(
      () => props.selected,
      (newValue: { low: number | null; high: number | null }) => {
        selectedPriceFrom.value = newValue.low
        selectedPriceTo.value = newValue.high
      }
    )

    watch(withPriceFacet, (newValue: Facet) => {
      isWithPriceOnlySelected.value = newValue?.isSelected
    })

    watch(discountFacet, (newValue: Facet) => {
      isWithDiscountsOnlySelected.value = newValue?.isSelected
    })

    watch(cheapOnlyFacet, (newValue: Facet) => {
      isCheapOnlySelected.value = newValue?.isSelected
    })

    watch(wholesalePriceFacet, (newValue: Facet) => {
      isWholesalePriceOnlySelected.value = newValue?.isSelected
    })

    watch(dealersOnlyFacet, (newValue: Facet) => {
      isDealersOnlySelected.value = newValue?.isSelected
    })

    const loadingFacets = computed(() => classifiedSearchState.loading.facets)

    const showButtonGroups = computed(
      () =>
        financeOptionsFacet.value?.visible || leasingOptionsFacet.value?.visible
    )

    const priceFromOptions = computed(() =>
      props.handler.searchables.values.map((s: any) => ({
        value: s.v,
        label: s.n,
        disabled:
          selectedPriceTo.value !== null &&
          parseFloat(s.v) > parseFloat(selectedPriceTo.value)
      }))
    )

    const priceToOptions = computed(() =>
      props.handler.searchables.values.map((s: any) => ({
        value: s.v,
        label: s.n,
        disabled:
          selectedPriceFrom.value !== null &&
          parseFloat(s.v) < parseFloat(selectedPriceFrom.value)
      }))
    )

    const purchaseTypeOptions = computed(() => {
      const options = [
        {
          value: 'price',
          label: t('in_cash')
        }
      ]

      if (financeOptionsFacet.value?.visible) {
        options.push({
          label: t('installments'),
          value: 'with_instalments'
        })
      }

      if (leasingOptionsFacet.value?.visible) {
        options.push({
          label: 'Leasing',
          value: 'leasing',
          variant: 'leasing'
        })
      }

      return options
    })

    const groupIsSelected = computed(
      () =>
        props.isSelected ||
        financeOptionsFacet.value?.isSelected ||
        withPriceFacet.value?.isSelected ||
        discountFacet.value?.isSelected ||
        wholesalePriceFacet.value?.isSelected ||
        cheapOnlyFacet.value?.isSelected ||
        dealersOnlyFacet.value?.isSelected ||
        selectedPurchaseType.value !== 'price'
    )

    function onPriceFromChange(value: string) {
      // Handles the case when price-from is less than price-to
      if (
        selectedPriceTo.value !== null &&
        value !== null &&
        value > selectedPriceTo.value
      ) {
        changeParams([
          { name: 'price', value: null },
          { name: 'price-from', value: selectedPriceTo.value },
          { name: 'price-to', value }
        ])
      } else {
        changeParams([
          { name: 'price', value: null },
          { name: 'price-from', value },
          { name: 'price-to', value: selectedPriceTo.value }
        ])
      }
    }

    function onPriceToChange(value: string) {
      // Handles the case when price-to is less than price-from
      if (
        selectedPriceFrom.value !== null &&
        value !== null &&
        value < selectedPriceFrom.value
      ) {
        changeParams([
          { name: 'price', value: null },
          { name: 'price-from', value },
          { name: 'price-to', value: selectedPriceFrom.value }
        ])
      } else {
        changeParams([
          { name: 'price', value: null },
          { name: 'price-from', value: selectedPriceFrom.value },
          { name: 'price-to', value }
        ])
      }
    }

    function getSelectedPurchaseType(
      selectedFinanceFacetValues: string[] = []
    ): PurchaseType {
      if (!selectedFinanceFacetValues) {
        return 'price'
      }

      if (selectedFinanceFacetValues.includes('with_instalments')) {
        return 'with_instalments'
      } else if (selectedFinanceFacetValues.includes('leasing')) {
        return 'leasing'
      }

      return 'price'
    }

    function onClearButtonClick() {
      changeParams([
        { name: 'sort', value: null },
        { name: 'price', value: null },
        { name: 'price-from', value: null },
        { name: 'price-to', value: null },
        { name: 'withprice', value: null },
        { name: 'discount', value: null },
        { name: 'wholesale', value: null },
        { name: 'dealersonly', value: null },
        { name: 'finance', value: null },
        { name: 'cheap', value: null },
        ...emptyInstallmentsParams,
        ...emptyLeasingParams
      ])
    }

    function onWithPriceOnlyChange(value: boolean) {
      changeParam({
        name: 'withprice',
        value
      })
    }

    function onDiscountsOnlyChange(value: boolean) {
      changeParam({
        name: 'discount',
        value
      })
    }

    function onWholesalePriceOnlyChange(value: boolean) {
      changeParam({
        name: 'wholesale',
        value
      })
    }

    function onDealersOnlyChange(value: boolean) {
      changeParam({
        name: 'dealersonly',
        value
      })
    }

    function onCheapOnlyChange(value: boolean) {
      changeParam({
        name: 'cheap',
        value
      })
    }

    function onMarketplaceChange(value: boolean) {
      changeParam({
        name: 'marketplace',
        value
      })
    }

    function onPurchaseTypeChange(purchaseType: PurchaseType) {
      if (purchaseType === 'leasing' || purchaseType === 'with_instalments') {
        changeParams([
          { name: 'withprice', value: null },
          { name: 'wholesale', value: null },
          { name: 'dealersonly', value: null },
          { name: 'payment_type', value: null },
          { name: 'cheap', value: null },
          { name: 'finance', value: purchaseType },
          {
            name: 'discount',
            value:
              purchaseType === 'leasing'
                ? null
                : isWithDiscountsOnlySelected.value
          },
          ...emptyInstallmentsParams,
          ...emptyLeasingParams
        ])
      } else {
        changeParams([
          { name: 'finance', value: null },
          { name: 'payment_type', value: null },
          ...emptyInstallmentsParams,
          ...emptyLeasingParams
        ])
      }
    }

    function onLeasingDataChange(data: {
      leaseFrom: string
      leaseTo: string
      downpaymentFrom: string
      downpaymentTo: string
      withVat: boolean
      duration: string
      mileage: string
      extras: string[]
    }) {
      changeParams([
        { name: 'lease-from', value: data.leaseFrom },
        { name: 'lease-to', value: data.leaseTo },
        { name: 'lease-downpayment-from', value: data.downpaymentFrom },
        { name: 'lease-downpayment-to', value: data.downpaymentTo },
        { name: 'with-vat', value: data.withVat },
        { name: 'duration', value: data.duration },
        { name: 'yearly-mileage', value: data.mileage },
        { name: 'leasing-extra', value: data.extras }
      ])
    }

    function onInstallmentsDataChange(data: {
      installmentFrom: string
      installmentTo: string
      installments: number
      downpayment: string
    }) {
      changeParams([
        { name: 'installment-from', value: data.installmentFrom },
        { name: 'installment-to', value: data.installmentTo },
        { name: 'installments', value: data.installments },
        { name: 'downpayment', value: data.downpayment }
      ])
    }

    return {
      ciCart,
      marketplaceFacet,
      financeFacet,
      leasingOptionsFacet,
      leasingExtrasFacet,
      dealersOnlyFacet,
      financeOptionsFacet,
      withPriceFacet,
      wholesalePriceFacet,
      discountFacet,
      cheapOnlyFacet,
      loadingFacets,
      groupIsSelected,
      showButtonGroups,
      purchaseTypeOptions,
      isCheapOnlySelected,
      selectedPurchaseType,
      selectedPriceFrom,
      selectedPriceTo,
      isWithPriceOnlySelected,
      isWithDiscountsOnlySelected,
      isWholesalePriceOnlySelected,
      isDealersOnlySelected,
      isMarketplaceSelected,
      priceFromOptions,
      priceToOptions,
      onPriceFromChange,
      onPriceToChange,
      onClearButtonClick,
      onWithPriceOnlyChange,
      onDiscountsOnlyChange,
      onWholesalePriceOnlyChange,
      onDealersOnlyChange,
      onCheapOnlyChange,
      onPurchaseTypeChange,
      onMarketplaceChange,
      onLeasingDataChange,
      onInstallmentsDataChange
    }
  },
  i18n: defineComponentTranslations({
    buy_online: {
      en: 'Instant buy',
      el: 'Άμεση αγορά'
    },
    new_marketplace_service: {
      en: 'New Instant buy Service',
      el: 'Νέα Υπηρεσία Άμεση αγορά!'
    },
    buy_products_directly: {
      en:
        'Now you can buy the product you are interested in, easily and quickly – without complicated procedures. (New products are constantly added)',
      el:
        'Τώρα μπορείς να αποκτήσεις το προϊόν που σε ενδιαφέρει, εύκολα και γρήγορα – χωρίς πολύπλοκες διαδικασίες. (Συνεχώς προστίθενται νέα προϊόντα)'
    }
  })
})
